export type HardwareConnectionMode = 'browser' | 'serial';

export interface PosLocalHardwarePrefs {
  scaleConnected?: boolean;
  scalePortLabel?: string;
  barcodePrinterMode: HardwareConnectionMode;
  barcodePrinterConnected?: boolean;
  barcodePrinterPortLabel?: string;
  receiptPrinterMode: HardwareConnectionMode;
  receiptPrinterConnected?: boolean;
  receiptPrinterPortLabel?: string;
  labelWidthMm: number;
  labelHeightMm: number;
}

const PREFS_KEY = 'saaspos_pos_hardware_prefs';

const DEFAULT_PREFS: PosLocalHardwarePrefs = {
  barcodePrinterMode: 'browser',
  receiptPrinterMode: 'browser',
  labelWidthMm: 50,
  labelHeightMm: 30,
};

export function loadLocalHardwarePrefs(): PosLocalHardwarePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function saveLocalHardwarePrefs(partial: Partial<PosLocalHardwarePrefs>): PosLocalHardwarePrefs {
  const next = { ...loadLocalHardwarePrefs(), ...partial };
  localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  return next;
}

export function serialSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}

/** ESC/POS cash drawer kick (pin 2). */
export function escPosCashDrawerKick(): Uint8Array {
  return new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);
}

/** Minimal ESC/POS init. */
export function escPosInit(): Uint8Array {
  return new Uint8Array([0x1b, 0x40]);
}

function parseScaleWeight(line: string): number | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/(\d+\.?\d*)/);
  if (!match) return null;
  const value = parseFloat(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

let scalePort: SerialPort | null = null;
let scaleReaderAbort: AbortController | null = null;
let printerPort: SerialPort | null = null;

export async function connectSerialScale(): Promise<string> {
  if (!serialSupported()) throw new Error('Web Serial is not supported in this browser');
  await disconnectSerialScale();
  const port = await navigator.serial!.requestPort();
  await port.open({ baudRate: 9600 });
  scalePort = port;
  scaleReaderAbort = new AbortController();
  void readScaleStream(port, scaleReaderAbort.signal);
  const label = `Scale @ 9600 baud`;
  saveLocalHardwarePrefs({ scaleConnected: true, scalePortLabel: label });
  return label;
}

export async function disconnectSerialScale(): Promise<void> {
  scaleReaderAbort?.abort();
  scaleReaderAbort = null;
  if (scalePort) {
    try {
      await scalePort.close();
    } catch {
      /* ignore */
    }
    scalePort = null;
  }
  saveLocalHardwarePrefs({ scaleConnected: false, scalePortLabel: undefined });
}

async function readScaleStream(port: SerialPort, signal: AbortSignal): Promise<void> {
  const reader = port.readable?.getReader();
  if (!reader) return;
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += new TextDecoder().decode(value);
      const parts = buffer.split(/[\r\n]+/);
      buffer = parts.pop() ?? '';
      for (const line of parts) {
        const weight = parseScaleWeight(line);
        if (weight != null) {
          window.dispatchEvent(new CustomEvent('pos-scale-weight', { detail: { weight } }));
        }
      }
    }
  } catch {
    /* closed */
  } finally {
    reader.releaseLock();
  }
}

export async function connectSerialPrinter(kind: 'barcode' | 'receipt'): Promise<string> {
  if (!serialSupported()) throw new Error('Web Serial is not supported in this browser');
  const port = await navigator.serial!.requestPort();
  await port.open({ baudRate: 9600 });
  printerPort = port;
  const label = `${kind === 'barcode' ? 'Label' : 'Receipt'} printer @ 9600 baud`;
  if (kind === 'barcode') {
    saveLocalHardwarePrefs({
      barcodePrinterMode: 'serial',
      barcodePrinterConnected: true,
      barcodePrinterPortLabel: label,
    });
  } else {
    saveLocalHardwarePrefs({
      receiptPrinterMode: 'serial',
      receiptPrinterConnected: true,
      receiptPrinterPortLabel: label,
    });
  }
  return label;
}

export async function disconnectSerialPrinter(kind: 'barcode' | 'receipt'): Promise<void> {
  if (printerPort) {
    try {
      await printerPort.close();
    } catch {
      /* ignore */
    }
    printerPort = null;
  }
  if (kind === 'barcode') {
    saveLocalHardwarePrefs({ barcodePrinterConnected: false, barcodePrinterPortLabel: undefined });
  } else {
    saveLocalHardwarePrefs({ receiptPrinterConnected: false, receiptPrinterPortLabel: undefined });
  }
}

export async function sendEscPos(data: Uint8Array): Promise<void> {
  if (!printerPort?.writable) throw new Error('No serial printer connected');
  const writer = printerPort.writable.getWriter();
  try {
    await writer.write(data);
  } finally {
    writer.releaseLock();
  }
}

export async function openCashDrawer(): Promise<void> {
  const prefs = loadLocalHardwarePrefs();
  if (prefs.receiptPrinterMode === 'serial' && prefs.receiptPrinterConnected && printerPort) {
    await sendEscPos(new Uint8Array([...escPosInit(), ...escPosCashDrawerKick()]));
    return;
  }
  throw new Error('Connect receipt printer via serial to kick cash drawer');
}

export function printHtmlDocument(html: string, windowName = 'pos-print'): void {
  const w = window.open('', windowName, 'width=360,height=480');
  if (!w) throw new Error('Popup blocked — allow popups for printing');
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export function markBarcodePrinterBrowserReady(): void {
  saveLocalHardwarePrefs({
    barcodePrinterMode: 'browser',
    barcodePrinterConnected: true,
    barcodePrinterPortLabel: 'System default (browser print)',
  });
}

export function markReceiptPrinterBrowserReady(): void {
  saveLocalHardwarePrefs({
    receiptPrinterMode: 'browser',
    receiptPrinterConnected: true,
    receiptPrinterPortLabel: 'System default (browser print)',
  });
}

export function isBarcodePrinterReady(prefs: PosLocalHardwarePrefs, enabled: boolean): boolean {
  return enabled && !!prefs.barcodePrinterConnected;
}
